import { NextPageContext } from 'next';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { createApolloClient } from '@/config/apollo-client';
import { getHostFromReq, getLangFromHost, isProduction } from '@/utils/helpers';
import { checkRoute } from '@/components/CustomLink/CustomLink.helpers';
import { QUERY } from '@/constants/common';
import { DeviceType, getDeviceType } from '@/utils/getDeviceType';
import { getServerI18n } from '@/i18n-server';

function checkIsReadyRoutes(context: NextPageContext, isDesktop: boolean) {
  // There may be some POST requests coming from the Ruby application,
  // and React should respond with a 404 when the request URL matches the URL of an existing page.
  // (For example, submitting an advertisement)
  if (context.req?.method === 'POST') return false;

  const asPath = context.asPath ?? '';

  const route = asPath.split('?')[0];
  const { query } = context;
  const { isInternal } = checkRoute(route, { query, isDesktop });
  return isInternal;
}

type InitialPropsData = Record<string, unknown>;

export function withPreRenderSettings(
  gip?: (
    context: NextPageContext,
    client: ApolloClient<NormalizedCacheObject>,
    deviceType: DeviceType
  ) => Promise<InitialPropsData> | InitialPropsData,
  isErrorPage?: boolean
) {
  return async (context: NextPageContext) => {
    if (context.res) {
      context.res.setHeader(
        'Cache-Control',
        's-maxage=1, stale-while-revalidate=59, max-age=0, private, must-revalidate'
      );
    }

    const host = context.req
      ? getHostFromReq(context.req)
      : window.location.host;
    const userAgent =
      (context.req
        ? context.req?.headers['user-agent']
        : window.navigator.userAgent) || '';
    const isSSRWebView = userAgent.indexOf('miniApp') >= 0;
    const { deviceType, device } = getDeviceType(userAgent);
    const currentLang = getLangFromHost(host);
    getServerI18n(currentLang);

    const accessToken = context.query?.[QUERY.access_token];

    const props = {
      currentLang,
      isSSRWebView,
      accessToken,
      device,
      deviceType,
    };

    if (!host) return props;

    const isRouteReady = checkIsReadyRoutes(
      context,
      deviceType === DeviceType.Desktop
    );

    if (!isRouteReady && isProduction() && !isErrorPage && context.res) {
      context.res.statusCode = 404;
      context.res.end();
      return;
    }

    if (!gip) return props;

    const apolloClient = createApolloClient({ lang: currentLang, context });

    const initialPropsData = await gip(context, apolloClient, deviceType);

    const apolloState = apolloClient.extract();

    return {
      ...initialPropsData,
      ...props,
      apolloState,
    };
  };
}
